import { useMemo } from 'react';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { withAlpha } from '../liveModeMeta';

// chart.js registration is idempotent — matches the per-file pattern used by
// ParameterChartsModal / OutcomePanel elsewhere in the app.
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler);

/**
 * Minimal, axis-less sparkline for the live rails. Fed from a plain number[]
 * (the simulator's ring buffer). No tooltips, grid, legend, or interaction.
 */
export function Sparkline({ data = [], color = '#1e4396', height = 36, animate = false }) {
  const chartData = useMemo(() => ({
    labels: data.map((_, i) => i),
    datasets: [{
      data,
      borderColor: color,
      borderWidth: 1.5,
      pointRadius: 0,
      tension: 0.35,
      fill: true,
      backgroundColor: (ctx) => {
        const { chart } = ctx;
        const { ctx: g, chartArea } = chart;
        if (!chartArea) return 'transparent';
        const grad = g.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
        grad.addColorStop(0, withAlpha(color, '33'));
        grad.addColorStop(1, withAlpha(color, '00'));
        return grad;
      },
    }],
  }), [data, color]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: animate ? { duration: 250 } : false,
    plugins: { legend: { display: false }, tooltip: { enabled: false } },
    scales: { x: { display: false }, y: { display: false } },
    elements: { line: { capBezierPoints: true } },
  }), [animate]);

  if (!data.length) return <div style={{ height }} aria-hidden />;

  return (
    <div style={{ height, width: '100%' }} aria-hidden>
      <Line data={chartData} options={options} />
    </div>
  );
}
